// client/src/pages/heartbeat/product-identity.tsx
//
// "Products" tab on each brand (next to Brand Identity). Shows each product's
// OWN visual identity — color swatches + packaging description, auto-detected
// from its photo by analyzeProductVisual — plus a Re-analyze button. When a
// campaign is scoped to a product, these override the brand colors so its ads
// use the right look (e.g. purple Supersleep instead of blue SuperCALM).

import { useState, useEffect, useRef } from "react";
import { useToast } from "@/hooks/use-toast";

interface ProductIdentityTabProps {
  brandId: number;
  /** When provided, renders an "Add product" button that calls this. */
  onAddProduct?: () => void;
  /** When provided, each product card renders an Edit button. */
  onEditProduct?: (product: ProductRow) => void;
  /** When provided, each product card renders a Delete button. */
  onDeleteProduct?: (product: ProductRow) => void;
  /** Bump to force a reload (e.g. after a product was added in the parent). */
  refreshSignal?: number;
}

export type ProductRow = {
  id: number;
  name: string;
  description: string | null;
  productUrl: string | null;
  colors: string[] | null;
  packagingDescription: string | null;
  visualAnalyzedAt: string | null;
  photos: string[] | null;
};

export function ProductIdentityTab({ brandId, onAddProduct, onEditProduct, onDeleteProduct, refreshSignal }: ProductIdentityTabProps) {
  const { toast } = useToast();
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [analyzingId, setAnalyzingId] = useState<number | null>(null);
  const [polls, setPolls] = useState(0);

  // v7.03 — SILENT REFRESH (Dan: "screen just goes blank and back while AI is
  // processing a product"). The background poll below fires every 3s while a
  // new product is being analyzed, and every call flipped the shared `loading`
  // flag — which gates the ENTIRE tab behind "Loading products…", so the whole
  // list unmounted and remounted ~10 times in a row. Only the first load (and
  // a brand switch) may blank the tab; refreshes swap the data underneath.
  const load = async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    try {
      const res = await fetch(`/api/brands/${brandId}/products`);
      const prods = res.ok ? await res.json() : [];
      setProducts(Array.isArray(prods) ? prods : []);
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  };

  // A brand switch must blank (the on-screen list belongs to the OLD brand);
  // a refreshSignal bump (product just added/deleted) refreshes in place —
  // the list on screen is still this brand's and only gains/loses a row.
  const loadedBrandRef = useRef<number | null>(null);
  useEffect(() => {
    setPolls(0);
    const brandChanged = loadedBrandRef.current !== brandId;
    loadedBrandRef.current = brandId;
    load({ silent: !brandChanged });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, refreshSignal]);

  // A freshly-added product analyzes in the background (~a few seconds). The
  // tab can load before that finishes and would otherwise sit on "not analyzed
  // yet" forever. Poll while any product has a photo but no colors yet, up to a
  // cap (so a genuinely-undetectable photo doesn't poll endlessly).
  useEffect(() => {
    const pending = products.some(
      (p) => (p.photos?.length ?? 0) > 0 && (!p.colors || p.colors.length === 0),
    );
    if (!pending || polls >= 10) return;
    const t = setTimeout(() => {
      setPolls((n) => n + 1);
      load({ silent: true });
    }, 3000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, polls]);

  const reanalyze = async (id: number) => {
    setAnalyzingId(id);
    try {
      const res = await fetch(`/api/products/${id}/analyze-visual`, { method: "POST" });
      if (!res.ok) throw new Error("failed");
      toast({ title: "Product re-analyzed", description: "Colors + packaging updated from the product photo." });
      // Silent: the row's own spinner (analyzingId) already reports progress —
      // blanking the whole tab to refresh one row is the v7.03 flicker.
      await load({ silent: true });
    } catch {
      toast({ title: "Re-analyze failed", variant: "destructive" });
    } finally {
      setAnalyzingId(null);
    }
  };

  if (loading) return <div className="p-6 text-sm text-slate-400">Loading products…</div>;

  return (
    <div className="p-5 space-y-4" data-testid="product-identity-tab">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">
            Product Identity
            {polls > 0 && <span className="ml-2 text-[10px] font-normal text-slate-400">scanning new product…</span>}
          </h3>
          <p className="text-[11px] text-slate-400 mt-0.5">
            Each product's own colors + packaging, auto-detected from its photo. When a campaign is scoped to a product, these override the brand colors so its ads use the right look.
          </p>
        </div>
        {onAddProduct && (
          <button
            onClick={onAddProduct}
            className="text-xs px-3 py-1.5 rounded bg-slate-900 text-white hover:bg-slate-800 flex-shrink-0"
          >
            + Add product
          </button>
        )}
      </div>

      {products.length === 0 ? (
        onAddProduct ? (
          <button
            onClick={onAddProduct}
            className="w-full rounded-lg border-2 border-dashed border-slate-200 py-8 text-center hover:border-slate-300"
            data-testid="button-products-empty-add"
          >
            <span className="block text-sm font-medium text-slate-500">No products yet</span>
            <span className="block text-xs text-slate-400 mt-1">+ Add a product — it scans automatically</span>
          </button>
        ) : (
          <p className="text-xs text-slate-400">No products yet.</p>
        )
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {products.map((p) => {
            const hasPhoto = (p.photos?.length ?? 0) > 0;
            const hasColors = !!p.colors && p.colors.length > 0;
            // "Processing" while a manual re-analyze runs, OR a freshly-added
            // product still has no colors and we're still polling for them.
            const processing = analyzingId === p.id || (hasPhoto && !hasColors && polls < 10);
            return (
            <div key={p.id} className={`rounded-lg border bg-white p-3 ${processing ? 'border-blue-200' : 'border-slate-200'}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  {p.photos?.[0] && (
                    <img src={p.photos[0]} alt="" className="w-10 h-10 rounded object-cover border border-slate-200 flex-shrink-0" />
                  )}
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-800 truncate">{p.name}</div>
                    <div className="text-[10px]">
                      {processing ? (
                        <span className="inline-flex items-center gap-1 text-blue-500 font-medium">
                          <span className="inline-block w-2.5 h-2.5 border-2 border-blue-200 border-t-blue-500 rounded-full animate-spin" />
                          Processing…
                        </span>
                      ) : (
                        <span className="text-slate-400">{p.visualAnalyzedAt ? `analyzed ${new Date(p.visualAnalyzedAt).toLocaleDateString()}` : 'not analyzed yet'}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    onClick={() => reanalyze(p.id)}
                    disabled={processing}
                    className="text-xs px-3 py-1.5 rounded bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50"
                  >
                    {processing ? 'Analyzing…' : 'Re-analyze'}
                  </button>
                  {onEditProduct && (
                    <button
                      onClick={() => onEditProduct(p)}
                      className="text-xs px-3 py-1.5 rounded bg-slate-100 text-slate-700 hover:bg-slate-200"
                      data-testid={`button-product-tab-edit-${p.id}`}
                    >
                      Edit
                    </button>
                  )}
                  {onDeleteProduct && (
                    <button
                      onClick={() => onDeleteProduct(p)}
                      className="text-xs px-3 py-1.5 rounded bg-red-50 text-red-600 hover:bg-red-100"
                      data-testid={`button-product-tab-delete-${p.id}`}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>

              {hasColors ? (
                <div className="flex items-center flex-wrap gap-1.5 mb-2">
                  {p.colors!.map((hex, i) => (
                    <span key={i} title={hex} className="inline-flex items-center">
                      <span className="inline-block w-7 h-7 rounded border border-slate-200" style={{ background: hex }} />
                    </span>
                  ))}
                  <span className="text-[10px] text-slate-400 ml-1 break-all">{p.colors!.join('  ')}</span>
                </div>
              ) : processing ? (
                <div className="flex items-center gap-2 text-[11px] text-blue-500 mb-2">
                  <span className="inline-block w-3 h-3 border-2 border-blue-200 border-t-blue-500 rounded-full animate-spin" />
                  Detecting colors + packaging…
                </div>
              ) : (
                <div className="text-[11px] text-slate-400 mb-2">No colors detected yet.</div>
              )}

              {p.packagingDescription && (
                <p className="text-[11px] text-slate-500 leading-snug">{p.packagingDescription}</p>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
